import { updateUnitContent, uploadUnitAsset, useUnitOverride } from '../../services/unitOverridesService';
import { useTTS } from '../../hooks/useTTS';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import ZoomableImage from '../../components/shared/ZoomableImage';
import UnitPageShell from '../../components/shared/UnitPageShell';
import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Paper, Box, IconButton, Button } from '@mui/material';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
// removed unused theme import to avoid lint warnings
const PAGE_ID = 'm6_u2_x1_wilo_yonos';

const M6_U2_X1_WiloYonos: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const textBlocks = useMemo(() => ([
    { key: 'intro', heading: '', body: 'Die Wilo Yonos PICO ist eine hocheffiziente Umwälzpumpe für Heizungs- und Klimaanwendungen in Ein- und Zweifamilienhäusern. Sie zeichnet sich durch Kompaktheit, Energieeinsparung und einfache Bedienung aus.' },
    { key: 'anschluss', heading: 'Anschluss der Yonos PICO', body: 'Elektrischer Anschluss:\n• Netzspannung: 1~230V, 50/60Hz (Typenschild beachten!).\n• Wilo-Connector: Vormontiertes Kabel für schnellen, werkzeuglosen und verpolungssicheren Anschluss.\n• Schutzleiter (PE) muss korrekt angeschlossen werden.\n\nHydraulischer Anschluss:\n• Gewindeanschlüsse (z.B. G 1, G 1 ½, G 2).\n• Einbaulage: Motorwelle horizontal, Wilo-Connector nicht nach unten zeigend.\n• Absperrarmaturen und Schmutzfänger empfohlen.' },
    { key: 'steuerung', heading: 'Steuerung der Yonos PICO', body: 'Die Steuerung erfolgt primär über den grünen Bedienknopf und die LED-Anzeige:\n\n• Regelungsarten (Auswahl per Drehknopf):\n  - Δp-c (Differenzdruck konstant - empfohlen für Heizkörper)\n  - Δp-v (Differenzdruck variabel)\n  - Konstante Drehzahl (3 Stufen)\n\n• Einstellung der Förderhöhe/Drehzahlstufe mit dem grünen Knopf.\n\n• LED-Anzeige für:\n  - Betriebszustand & gewählte Regelungsart\n  - Eingestellte Förderhöhe/Drehzahlstufe\n  - Aktueller Stromverbrauch in Watt\n  - Warn- und Fehlermeldungen (Blinkcodes)\n\n• Entlüftungsfunktion: Oft durch längeres Drücken des grünen Knopfes aktivierbar.\n\n• Externe Ansteuerung (PWM, 0-10V): Nicht bei Standard Yonos PICO Modellen. Nur bei speziellen Varianten (z.B. mit Zusatz \'-N\') vorhanden. Für die Basis-PICO ist keine externe Steuerung vorgesehen.' },
    { key: 'wichtig', heading: 'Wichtige Hinweise', body: '• Bedienungsanleitung des exakten Modells beachten!\n• Richtige Regelungsart und Förderhöhe wählen.\n• Nach Installation Entlüftungsfunktion nutzen.\n• Standardmodelle haben keine externe Ansteuerung.' }
  ]), []);

  const images = [
    { key: 'img_yonos_front', src: '/assets/drawable/pumpe_pico2.png', label: 'Wilo Yonos PICO Frontansicht mit Bedienknopf' },
    { key: 'img_yonos_connector', src: '/assets/drawable/pumpe_pico_stecker.png', label: 'Wilo-Connector und typische Einbausituation' },
    { key: 'img_yonos_bedienfeld', src: '/assets/drawable/pumpe_pico3.png', label: 'Bedienfeld der Wilo Yonos PICO mit LEDs' }
  ];

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = useMemo(
    () => textBlocks.map(tb => ({ ...tb, body: override?.texts?.[tb.key] || tb.body })),
    [override, textBlocks]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullText = useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullText });

  const handleTextSave = useCallback(async (blockKey: string, newBody: string) => {
    if (!token) return;
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockKey]: newBody } }, token);
      alert('Gespeichert.');
      await refresh();
    } catch (e) {
      console.error(e);
      alert('Fehler beim Speichern.');
    }
  }, [token, refresh]);

  const handleImageReplace = useCallback(async (imageKey: string, file: File) => {
    if (!token) return;
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild aktualisiert.');
      await refresh();
    } catch (e) {
      console.error(e);
      alert('Fehler beim Upload.');
    }
  }, [token, refresh]);

  const ttsBtn = React.createElement(IconButton, {
    onClick: speaking ? stop : speak,
    color: 'primary',
    sx: { ml: 1 }
  }, React.createElement(VolumeUpIcon));

  return React.createElement(UnitPageShell, {
    title: '6.2.1 Wilo Yonos PICO',
    actions: ttsBtn
  }, React.createElement(Paper, { elevation: 0, sx: { backgroundColor: 'transparent' } },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, {
          key: img.key,
          src: imgSrc(img.key, img.src),
          label: img.label,
          onReplace: canUploadImages ? (file) => handleImageReplace(img.key, file) : undefined
        }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 3 } },
        mergedBlocks.map(block => React.createElement(EditableTextBlock, {
          key: block.key,
          pageId: PAGE_ID,
          blockId: block.key,
          heading: block.heading,
          body: block.body,
          onSave: canEditTexts ? (body: string) => handleTextSave(block.key, body) : undefined
        }))
      )
    ),
    React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } },
      React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m6_u2_x99_quiz') }, 'Zum Quiz')
    )
  ));
};

export default M6_U2_X1_WiloYonos;


